import { Alert, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Space, Tag } from "antd";
import { useState } from "react";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { CreditCardOutlined, EditOutlined, SaveOutlined, CheckCircleOutlined, InboxOutlined } from "@ant-design/icons";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import { LOGGED_USER } from "../../../../variables/constants";
import dayjs from "dayjs";

export const ChequeRetrocession = ({ retrocession, onUpdate, isPool: isPoolProp }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    // Accusé de réception (compagnie) : saisie du représentant + confirmation.
    const [ackMode, setAckMode] = useState(false);
    const [ackRepresentant, setAckRepresentant] = useState("");
    const [acking, setAcking] = useState(false);
    const [form] = Form.useForm();

    const isPool = isPoolProp !== undefined
        ? isPoolProp
        : LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    const hasCheque = retrocession.numeroCheque && retrocession.banque;
    const accuse = !!retrocession.accuseReceptionLe;

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            await axiosInstance.put(
                `/api/retrocession/${retrocession.retrocessionId}/cheque`,
                {
                    banque: values.banque,
                    numeroCheque: values.numeroCheque,
                    dateCheque: values.dateCheque?.format('YYYY-MM-DD'),
                    montantCheque: values.montantCheque
                }
            );
            toast.success('Chèque enregistré avec succès');
            setEditing(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            if (err.errorFields) return;
            console.error(err);
            toast.error("Erreur lors de l'enregistrement du chèque");
        } finally {
            setSaving(false);
        }
    };

    // Accusé de réception du chèque du Pool par la compagnie.
    const handleAccuser = async () => {
        try {
            setAcking(true);
            await axiosInstance.put(
                `/api/retrocession/${retrocession.retrocessionId}/accuser-reception`,
                { representant: ackRepresentant?.trim() || null }
            );
            toast.success("Réception du chèque confirmée");
            setAckMode(false);
            setAckRepresentant("");
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Erreur lors de l'accusé de réception");
        } finally {
            setAcking(false);
        }
    };

    const startEdit = () => {
        form.setFieldsValue({
            banque: retrocession.banque,
            numeroCheque: retrocession.numeroCheque,
            dateCheque: retrocession.dateCheque ? dayjs(retrocession.dateCheque) : null,
            montantCheque: retrocession.montantCheque
        });
        setEditing(true);
    };

    // Bloc « accusé de réception » (affiché dès qu'un chèque est renseigné).
    const blocAccuse = hasCheque && (
        <div style={{ marginTop: 12 }}>
            {accuse ? (
                <Alert
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    message={
                        <span>
                            Chèque <b>réceptionné</b> le {dayjs(retrocession.accuseReceptionLe).format('DD/MM/YYYY à HH:mm')}
                            {retrocession.accuseReceptionRepresentant ? <> — par <b>{retrocession.accuseReceptionRepresentant}</b></> : null}
                        </span>
                    }
                />
            ) : isPool ? (
                <Tag color="orange" style={{ padding: '4px 10px' }}>
                    <InboxOutlined /> En attente de l'accusé de réception de la compagnie
                </Tag>
            ) : ackMode ? (
                <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
                    <Input
                        placeholder="Nom du réceptionnaire (facultatif)"
                        value={ackRepresentant}
                        onChange={(e) => setAckRepresentant(e.target.value)}
                    />
                    <Button type="primary" loading={acking} icon={<CheckCircleOutlined />} onClick={handleAccuser}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                        Confirmer la réception
                    </Button>
                    <Button onClick={() => setAckMode(false)}>Annuler</Button>
                </Space.Compact>
            ) : (
                <Button type="primary" icon={<InboxOutlined />} onClick={() => setAckMode(true)}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                    Accuser réception du chèque
                </Button>
            )}
        </div>
    );

    // --- Mode affichage (chèque déjà renseigné) ---
    if (hasCheque && !editing) {
        return (
            <>
                <Divider>
                    <CreditCardOutlined /> Chèque de Rétrocession
                </Divider>
                <Card
                    size="small"
                    style={{ borderLeft: '4px solid #52c41a', background: '#f6ffed', marginBottom: 20 }}
                    extra={isPool ? (
                        <Button type="link" icon={<EditOutlined />} onClick={startEdit}>Modifier</Button>
                    ) : null}
                >
                    <Row gutter={[16, 12]}>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Banque</div>
                            <div style={{ fontWeight: 600 }}>{retrocession.banque}</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>N° Chèque</div>
                            <div style={{ fontWeight: 600 }}>{retrocession.numeroCheque}</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Date d'émission</div>
                            <div style={{ fontWeight: 600 }}>
                                {retrocession.dateCheque ? dayjs(retrocession.dateCheque).format('DD/MM/YYYY') : '-'}
                            </div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Montant</div>
                            <div style={{ fontWeight: 600, color: '#52c41a' }}>
                                {displayNumbers(retrocession.montantCheque || 0)} F CFA
                            </div>
                        </Col>
                    </Row>
                    {blocAccuse}
                </Card>
            </>
        );
    }

    // --- Mode formulaire (assignation par le Pool) ou attente (compagnie) ---
    return (
        <>
            <Divider>
                <CreditCardOutlined /> Chèque de Rétrocession
            </Divider>
            {!hasCheque && !editing && (
                isPool ? (
                    <Alert
                        message="Aucun chèque renseigné pour cette rétrocession"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        action={
                            <Button type="primary" size="small"
                                onClick={() => { form.resetFields(); setEditing(true); }}>
                                Renseigner le chèque
                            </Button>
                        }
                    />
                ) : (
                    <Alert
                        message="En attente du chèque du Pool"
                        description="Le chèque de rétrocession n'a pas encore été émis par le Pool. Vous pourrez en accuser réception dès qu'il sera renseigné."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )
            )}
            {editing && isPool && (
                <Card size="small" style={{ borderLeft: '4px solid #1890ff', background: '#f0f5ff', marginBottom: 20 }}>
                    <Form form={form} layout="vertical"
                        initialValues={{
                            banque: retrocession.banque,
                            numeroCheque: retrocession.numeroCheque,
                            dateCheque: retrocession.dateCheque ? dayjs(retrocession.dateCheque) : null,
                            montantCheque: retrocession.montantCheque
                        }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item name="banque" label="Banque" rules={[{ required: true, message: 'Requis' }]}>
                                    <Input placeholder="Ex: ECOBANK" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="numeroCheque" label="N° Chèque" rules={[{ required: true, message: 'Requis' }]}>
                                    <Input placeholder="Ex: CHQ-00123" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="dateCheque" label="Date d'émission" rules={[{ required: true, message: 'Requis' }]}>
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Sélectionner"
                                        disabledDate={current => current && current > dayjs().endOf('day')} />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="montantCheque" label="Montant (F CFA)" rules={[{ required: true, message: 'Requis' }]}>
                                    <InputNumber style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        parser={value => value.replace(/\s/g, '')} placeholder="0" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Space>
                            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>Enregistrer</Button>
                            <Button onClick={() => setEditing(false)}>Annuler</Button>
                        </Space>
                    </Form>
                </Card>
            )}
        </>
    );
};
